/*
 * Copyright (C) 2025 SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useCallback, useEffect, useState } from "react";
import { VIEWPORT_SIZES_MOBILE, VIEWPORT_SIZES_DESKTOP, useViewport } from "cms/hooks/use-window-size";
import { Table } from "./table";
import { SearchFilterBar } from "./search-filter-bar";
import { MobileGrid } from "./grid";
import { useAuth } from "cms/auth/hooks/useAuth";
import { isStringNotEmpty } from "cms/utils/empty-exists";
import { SimpliCitySDK } from "cms/sdk";

const DataTableComponent = (props) => {
    const componentConfig = props?.component;
    const dataTableTitle = componentConfig?.title;
    const dataTableSubtitle = componentConfig?.subtitle;

    // Current viewport tracking
    const currentViewportSize = useViewport()?.viewportSize;
    const { token } = useAuth();

    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        total: 0,
        pages: 1,
        limit: 10,
        offset: 0,
        page: 1,
        pageSize: 10
    });
    // Shared state for search and filter
    const [currentSearchInput, setCurrentSearchInput] = useState("");
    const [activeSearchQuery, setActiveSearchQuery] = useState("");
    const [currentSelectedMunicipality, setCurrentSelectedMunicipality] = useState("All municipalities");

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Fetch data from API
    const fetchDisputeData = useCallback(async () => {
        try {
            const searchTermToUse = isStringNotEmpty(activeSearchQuery)
                ? activeSearchQuery
                : undefined;
            const params = {
                page: currentPage,
                pageSize: pageSize,
            };
            const type = "entity.dispute";
            const attributes = [];
            if (currentSelectedMunicipality !== "All municipalities") {
                attributes.push({ key: "municipality", value: currentSelectedMunicipality });
            }
            const payload = {
                term: searchTermToUse,
                attributes
            };
            const response = await SimpliCitySDK.entity.searchByType(
                token,
                type,
                payload,
                params
            );

            if (response && response.results) {
                setData(response.results);
                setPagination(response.pagination || {
                    total: 0,
                    pages: 1,
                    limit: 10,
                    offset: 0,
                    page: 1,
                    pageSize: 10
                });
            }
        } catch (error) {
            setData([]);
        }
    }, [activeSearchQuery, currentSelectedMunicipality, token, currentPage, pageSize]);

    useEffect(() => {
        fetchDisputeData();
    }, [fetchDisputeData]);

    const handleSearchInputChange = (newSearchInput) => {
        setCurrentSearchInput(newSearchInput);
    };

    const handleSearchTrigger = () => {
        setActiveSearchQuery(currentSearchInput);
        setCurrentPage(1); // Reset to first page when searching
    };

    const handleMunicipalityChange = (selectedMunicipalityValue) => {
        setCurrentSelectedMunicipality(selectedMunicipalityValue);
        setCurrentPage(1); // Reset to first page when filtering
    };

    // Pagination handlers
    const handlePageChange = (event, newPage) => {
        setCurrentPage(newPage);
    };

    const handlePageSizeChange = (event) => {
        setPageSize(parseInt(event?.target?.value, 10));
        setCurrentPage(1); // Reset to first page when changing page size
    };

    const isMobileViewport = VIEWPORT_SIZES_MOBILE.includes(currentViewportSize);
    const isDesktopViewport = VIEWPORT_SIZES_DESKTOP.includes(currentViewportSize);

    return (
        <div className="simplicity-data-table">
            {dataTableTitle && <h1 className="simplicity-data-table-title">{dataTableTitle}</h1>}
            {dataTableSubtitle && <div className="simplicity-data-table-subtitle">{dataTableSubtitle}</div>}
            <div className="data-table-search-filter-container">
                <SearchFilterBar
                    onSearchInputChange={handleSearchInputChange}
                    onSearchTrigger={handleSearchTrigger}
                    onMunicipalityChange={handleMunicipalityChange}
                    searchInput={currentSearchInput}
                    selectedMunicipality={currentSelectedMunicipality}
                />
            </div>
            {isDesktopViewport && (
                <div className="data-table-container">
                    <Table
                        searchQuery={activeSearchQuery}
                        selectedMunicipality={currentSelectedMunicipality}
                        data={data}
                        pagination={pagination}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                    />
                </div>
            )}
            {isMobileViewport && (
                <MobileGrid
                    searchQuery={activeSearchQuery}
                    selectedMunicipality={currentSelectedMunicipality}
                    data={data}
                    pagination={pagination}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            )}
        </div>
    );
};

DataTableComponent.displayName = "DataTableComponent";
export { DataTableComponent };
